// qs-useFileOperations.ts
import { emitForceCanvasReinit } from '@/composables/qs-useEventBus'
import { useDrawingManagement } from '@/composables/qs-useDrawingManagement'
import { useHistory } from '@/composables/qs-useHistory'
import { forceSetDefaultBrushSize } from '@/config/qs-brushDefaults'
import type { Drawing } from '@/stores/qs-store-gallery'
import type { TextObject } from '@/types/qs-types-index'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useGalleryStore } from '@/stores/qs-store-gallery'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useToolStore } from '@/stores/qs-store-tools'
import { nextTick } from 'vue'

export function useFileOperations() {
  const toolStore = useToolStore()
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const galleryStore = useGalleryStore()
  const history = useHistory()

  const dispatchFileChangeEvent = (): void => {
    window.dispatchEvent(new CustomEvent('file-changed'))
  }

  const resetStoresToDefault = (): void => {
    canvasStore.resetView()
    canvasStore.setBackgroundColor(toolStore.backgroundColor)
    forceSetDefaultBrushSize()
  }

  const forceCanvasReinitialization = (): void => {
    nextTick(() => {
      if (canvasStore.stage) {
        emitForceCanvasReinit()
      }
    })
  }

  const triggerDelayedCanvasReinit = (): void => {
    nextTick(() => {
      setTimeout(() => {
        if (canvasStore.stage) {
          window.dispatchEvent(new CustomEvent('force-canvas-reinit'))
        }
      }, 50)
    })
  }

  const resetAllPanelContent = (): void => {
    resetStoresToDefault()
    forceCanvasReinitialization()
  }

  const createNewDrawing = (): Drawing | null => {
    return galleryStore.createNewDrawing(undefined, toolStore.backgroundColor)
  }

  const saveCurrentDrawing = async (): Promise<void> => {
    const currentDrawing = galleryStore.currentDrawing
    if (!currentDrawing) return

    let textObjects: TextObject[] = []
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()
      textObjects = textStore.textObjects ?? []
    } catch {
      textObjects = []
    }

    galleryStore.saveCurrentDrawing(
      layerStore.layers,
      {
        scale: canvasStore.scale,
        position: canvasStore.position,
        backgroundColor: canvasStore.backgroundColor
      },
      {
        currentTool: toolStore.currentTool,
        backgroundColor: toolStore.backgroundColor,
        colorSwatches: toolStore.colorSwatches,
        brushPresets: toolStore.brushPresets
      },
      textObjects
    )
  }

  const loadDrawing = (drawing: Drawing | null | undefined): void => {
    if (!drawing) return

    const { loadDrawing: loadDrawingFromManagement } = useDrawingManagement()
    loadDrawingFromManagement(drawing)
    triggerDelayedCanvasReinit()
  }

  const handleNewFile = async (): Promise<void> => {
    dispatchFileChangeEvent()
    await saveCurrentDrawing()
    resetAllPanelContent()
    history.clearHistory()

    const newDrawing = createNewDrawing()
    if (newDrawing) {
      loadDrawing(newDrawing)
      await history.initializeHistory()
    }
  }

  const handleOpenFile = (): void => {
    galleryStore.toggleSidebar()
  }

  const handleDuplicateFile = async (): Promise<void> => {
    dispatchFileChangeEvent()

    const currentDrawing = galleryStore.currentDrawing
    if (!currentDrawing) return

    await saveCurrentDrawing()

    const duplicatedDrawing = galleryStore.duplicateDrawing(currentDrawing.id)
    if (duplicatedDrawing) {
      history.clearHistory()
      loadDrawing(duplicatedDrawing)
      await history.initializeHistory()
    }
  }

  return {
    handleNewFile,
    handleOpenFile,
    handleDuplicateFile,
    saveCurrentDrawing,
    loadDrawing,
    resetAllPanelContent
  }
}